import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { imgBaseUrl } from "../../api/api";
import {
  assignmentStudentList,
  assignmentSubmissionShow,
} from "../../api/AssignmentService";

import { LoadingDiv, SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import usePrivateLink from "../config/usePrivateLink";
import Pagination from "../components/Pagination";
import AcademicHeader from "../Academic/AcademicHeader";

export default function AssignmentView() {
  usePrivateLink();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const id = queryParams.get("id");
  const type = queryParams.get("type");

  const [loading, setLoading] = useState(false);

  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({});

  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);

  const [submissionData, setSubmissionData] = useState(null);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [loadingRowId, setLoadingRowId] = useState(null);

  const getList = async () => {
    setLoading(true);

    try {
      let addUrl = `assignment_id=${id}`;
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value) {
          addUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });
      const response = await assignmentStudentList(addUrl);
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getFileType = (file) => {
    if (!file) return "";

    const ext = file.split(".").pop().toLowerCase();

    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
    if (["mp4", "webm", "ogg"].includes(ext)) return "video";
    if (["pdf"].includes(ext)) return "pdf";

    return "other";
  };

  // 🔹 Fetches the actual submission (answer / feedback / file / status)
  // from assignment_submission_show_v2 and opens the detail modal.
  const getSubmission = async (item) => {
    setSubmissionLoading(true);
    setSubmissionData(null);
    setSelectedItem(item);
    setLoadingRowId(item?.id);
    setShowDetailModal(true);

    try {
      const userId = item?.id;

      const userType = type?.toLowerCase() === "staff" ? "staff" : "student";

      const response = await assignmentSubmissionShow(
        `assignment_id=${id}&user_id=${userId}&type=${userType}`,
      );

      if (response?.data?.status) {
        setSubmissionData(response?.data?.data || null);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
        setShowDetailModal(false);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
      setShowDetailModal(false);
    } finally {
      setSubmissionLoading(false);
      setLoadingRowId(null);
    }
  };

  const totalBatches = data?.target_info?.batch_names?.length ?? 0;
  const totalStudents = data?.data?.total ?? data?.data?.data?.length ?? 0;
  const submittedCount =
    data?.data?.data?.filter((s) => s?.assignment_submit_status === "Yes")
      .length ?? 0;
  const pendingCount = Math.max(totalStudents - submittedCount, 0);

  const initials = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join("");

  useEffect(() => {
    getList();
  }, [id, type, filters]);

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row mt-4">
                  <AcademicHeader />
                  <div className="col-12">
                    {/* 🔹 Assignment Header */}
                    <div className="card mb-4 shadow-sm border-0 overflow-hidden">
                      {/* Top strip: icon, title, meta badges, attachment */}
                      <div className="card-body p-4 pb-0">
                        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <span
                              className="d-inline-flex align-items-center justify-content-center rounded-circle bg-label-primary flex-shrink-0"
                              style={{ width: 48, height: 48 }}
                            >
                              <i className="ri-file-text-line ri-lg text-primary"></i>
                            </span>
                            <div>
                              <h4 className="text-primary fw-bold mb-1">
                                {data?.title || "—"}
                              </h4>
                              <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-label-primary">
                                  <i className="ri-price-tag-3-line me-1"></i>
                                  {data?.type}
                                </span>
                                <span
                                  className={`badge ${
                                    data?.allow_submission === "Yes"
                                      ? "bg-label-success"
                                      : "bg-label-danger"
                                  }`}
                                >
                                  <i className="ri-upload-2-line me-1"></i>
                                  Submission: {data?.allow_submission}
                                </span>
                                <span className="badge bg-label-secondary">
                                  <i className="ri-calendar-event-line me-1"></i>
                                  Due: {data?.due_date}
                                </span>
                                {data?.target_info?.subject?.name && (
                                  <span className="badge bg-label-warning">
                                    <i className="ri-book-2-line me-1"></i>
                                    {data.target_info.subject.name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {data?.file && (
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => {
                                setFileUrl(data?.file);
                                setShowImageModal(true);
                              }}
                            >
                              <i className="ri-attachment-2 me-1"></i>
                              View Attachment
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Stat strip: total batches / submitted / pending */}
                      <div className="px-4 mt-4">
                        <div className="row g-3">
                          {totalBatches > 0 && (
                            <div className="col-6 col-md-3">
                              <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-dark h-100">
                                <span
                                  className="d-inline-flex align-items-center justify-content-center rounded-circle bg-dark bg-opacity-10 flex-shrink-0"
                                  style={{ width: 40, height: 40 }}
                                >
                                  <i className="ri-group-line fs-5"></i>
                                </span>
                                <div>
                                  <div className="fs-5 fw-bold lh-1">
                                    {totalBatches}
                                  </div>
                                  <small className="text-muted">
                                    Total Batches
                                  </small>
                                </div>
                              </div>
                            </div>
                          )}

                          {data?.allow_submission === "Yes" && (
                            <>
                              <div className="col-6 col-md-3">
                                <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-success h-100">
                                  <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 flex-shrink-0"
                                    style={{ width: 40, height: 40 }}
                                  >
                                    <i className="ri-checkbox-circle-line fs-5 text-success"></i>
                                  </span>
                                  <div>
                                    <div className="fs-5 fw-bold lh-1">
                                      {submittedCount}
                                    </div>
                                    <small className="text-muted">
                                      Submitted
                                    </small>
                                  </div>
                                </div>
                              </div>

                              <div className="col-6 col-md-3">
                                <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-warning h-100">
                                  <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-10 flex-shrink-0"
                                    style={{ width: 40, height: 40 }}
                                  >
                                    <i className="ri-time-line fs-5 text-warning"></i>
                                  </span>
                                  <div>
                                    <div className="fs-5 fw-bold lh-1">
                                      {pendingCount}
                                    </div>
                                    <small className="text-muted">
                                      Pending
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="card-body p-4 pt-3">
                        <hr className="mt-3 mb-4" />

                        {/* 🔹 Target Info — batch names */}
                        {data?.target_info?.batch_names?.length > 0 && (
                          <div className="mb-4">
                            <small className="text-muted text-uppercase fw-bold d-flex align-items-center gap-1 mb-2">
                              <i className="ri-git-branch-line"></i>
                              {data.target_info?.label || "Target Batches"}
                            </small>
                            <div className="d-flex flex-wrap gap-2">
                              {data.target_info.batch_names.map(
                                (bname, idx) => (
                                  <span
                                    className="badge bg-label-dark px-3 py-2"
                                    key={
                                      data.target_info.batch_ids?.[idx] ?? idx
                                    }
                                  >
                                    <i
                                      className="ri-checkbox-blank-circle-fill me-2 text-primary"
                                      style={{ fontSize: 6 }}
                                    ></i>
                                    {bname}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                        <div>
                          <small className="text-muted text-uppercase fw-bold d-flex align-items-center gap-1 mb-2">
                            <i className="ri-file-text-line"></i>
                            Description
                          </small>
                          <p className="mb-0">
                            {data?.description || "No description provided."}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 📋 Recipient Table */}
                    <div className="card shadow-sm border-0">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <h5 className="mb-0 text-uppercase fw-bold d-flex align-items-center gap-2">
                            <i className="ri-team-line text-primary"></i>
                            {type} List
                            <span className="badge bg-label-secondary rounded-pill ms-1">
                              {data?.data?.total ?? 0}
                            </span>
                          </h5>

                          <div
                            className="input-group"
                            style={{ maxWidth: 280 }}
                          >
                            <span className="input-group-text bg-transparent">
                              <i className="ri-search-line"></i>
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search name / mobile..."
                              value={filters?.search || ""}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  search: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead>
                              <tr className="text-muted text-uppercase small">
                                <th>#</th>
                                <th>Name</th>
                                <th>Mobile</th>
                                <th>Status</th>
                                <th className="">Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {data?.data?.data?.length > 0 ? (
                                data.data.data.map((item, i) => {
                                  const submitted =
                                    item?.assignment_submit_status === "Yes";

                                  return (
                                    <tr key={item?.id ?? i}>
                                      <td className="text-muted">{i + 1}</td>
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          <span
                                            className="d-inline-flex align-items-center justify-content-center rounded-circle bg-label-primary text-primary fw-semibold flex-shrink-0"
                                            style={{
                                              width: 32,
                                              height: 32,
                                              fontSize: 12,
                                            }}
                                          >
                                            {initials(
                                              item?.name ||
                                                item?.student_name ||
                                                item?.staff_name,
                                            )}
                                          </span>
                                          <div>
                                            <div className="fw-semibold">
                                              {item?.name ||
                                                item?.student_name ||
                                                item?.staff_name}
                                            </div>
                                            {item?.registration_no && (
                                              <small className="text-muted">
                                                Reg: {item.registration_no}
                                              </small>
                                            )}
                                          </div>
                                        </div>
                                      </td>

                                      <td>
                                        {item?.mobile || item?.phone || "-"}
                                      </td>

                                      <td>
                                        {data?.allow_submission === "Yes" ? (
                                          <span
                                            className={`badge ${
                                              submitted
                                                ? "bg-label-success"
                                                : "bg-label-warning"
                                            }`}
                                          >
                                            <i
                                              className={`me-1 ${
                                                submitted
                                                  ? "ri-checkbox-circle-line"
                                                  : "ri-time-line"
                                              }`}
                                            ></i>
                                            {submitted
                                              ? "Submitted"
                                              : "Pending"}
                                          </span>
                                        ) : (
                                          <span className="text-muted">-</span>
                                        )}
                                      </td>

                                      <td className="">
                                        {data?.allow_submission === "Yes" &&
                                        submitted ? (
                                          <button
                                            className="btn btn-sm btn-outline-primary"
                                            disabled={loadingRowId === item?.id}
                                            onClick={() => getSubmission(item)}
                                          >
                                            {loadingRowId === item?.id ? (
                                              <span
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                              ></span>
                                            ) : (
                                              <>
                                                <i className="ri-eye-line me-1"></i>
                                                View
                                              </>
                                            )}
                                          </button>
                                        ) : (
                                          <span className="text-muted">-</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td
                                    colSpan={5}
                                    className="text-center text-muted py-5"
                                  >
                                    <i className="ri-inbox-line d-block fs-3 mb-2"></i>
                                    No records found
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>

                          <Pagination
                            data={data?.data}
                            setFilters={setFilters}
                            filters={filters}
                          />

                          {showImageModal && (
                            <>
                              <div
                                className="modal fade show d-block"
                                style={{
                                  zIndex: 9999,
                                }}
                              >
                                <div className="modal-dialog modal-lg modal-dialog-centered">
                                  <div className="modal-content border-0 shadow rounded-4">
                                    <div className="modal-header">
                                      <h5 className="fw-bold text-primary mb-0">
                                        File Preview
                                      </h5>

                                      <button
                                        className="btn-close"
                                        onClick={() => setShowImageModal(false)}
                                      ></button>
                                    </div>

                                    <div className="modal-body text-center">
                                      {fileUrl ? (
                                        <>
                                          {getFileType(fileUrl) === "image" && (
                                            <img
                                              src={`${imgBaseUrl}${fileUrl}`}
                                              className="img-fluid rounded shadow"
                                              style={{ maxHeight: "500px" }}
                                            />
                                          )}

                                          {getFileType(fileUrl) === "video" && (
                                            <video
                                              controls
                                              className="w-100 rounded shadow"
                                              style={{ maxHeight: "500px" }}
                                            >
                                              <source
                                                src={`${imgBaseUrl}${fileUrl}`}
                                              />
                                            </video>
                                          )}

                                          {getFileType(fileUrl) === "pdf" && (
                                            <iframe
                                              src={`${imgBaseUrl}${fileUrl}`}
                                              width="100%"
                                              height="500px"
                                              title="PDF"
                                              className="rounded shadow"
                                            />
                                          )}

                                          {getFileType(fileUrl) === "other" && (
                                            <a
                                              href={`${imgBaseUrl}${fileUrl}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="btn btn-primary"
                                            >
                                              <i className="ri-download-2-line me-1"></i>
                                              Download File
                                            </a>
                                          )}
                                        </>
                                      ) : (
                                        <p className="text-muted">
                                          No File Available
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div
                                className="modal-backdrop fade show"
                                style={{
                                  zIndex: 9998,
                                }}
                              ></div>
                            </>
                          )}

                          {/* 🔹 Submission Detail Modal — data comes from assignmentSubmissionShow */}
                          {showDetailModal && (
                            <>
                              <div className="modal fade show d-block">
                                <div className="modal-dialog modal-lg modal-dialog-centered">
                                  <div className="modal-content border-0 shadow rounded-4 overflow-hidden">
                                    <div
                                      className="p-4 text-white"
                                      style={{
                                        background:
                                          "linear-gradient(137deg, rgb(106 90 249 / 8%), rgb(153 110 213 / 73%), rgb(213 110 110 / 58%))",
                                      }}
                                    >
                                      <div className="d-flex justify-content-end">
                                        <button
                                          className="btn-close btn-close-white"
                                          onClick={() => {
                                            setShowDetailModal(false);
                                            setSubmissionData(null);
                                            setSelectedItem(null);
                                          }}
                                        ></button>
                                      </div>

                                      <div className="d-flex align-items-center justify-content-center">
                                        <div
                                          className="rounded-circle bg-white text-primary d-flex align-items-center justify-content-center fw-bold"
                                          style={{
                                            width: "60px",
                                            height: "60px",
                                            fontSize: "22px",
                                          }}
                                        >
                                          {initials(selectedItem?.name)}
                                        </div>

                                        <div className="ms-3">
                                          <h5 className="mb-0">
                                            {selectedItem?.name}
                                          </h5>
                                          <small>
                                            Reg: {selectedItem?.registration_no}
                                          </small>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="modal-body">
                                      {submissionLoading ? (
                                        <div className="text-center py-5">
                                          <span
                                            className="spinner-border text-primary mb-2"
                                            role="status"
                                          ></span>
                                          <div className="text-muted small">
                                            Loading submission...
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          {/* Info Cards */}
                                          <div className="row mb-4">
                                            <div className="col-md-6 mb-3">
                                              <div className="p-3 border rounded-3 h-100">
                                                <small className="text-muted">
                                                  <i className="ri-smartphone-line me-1"></i>
                                                  Mobile
                                                </small>
                                                <div className="fw-semibold fs-5">
                                                  {selectedItem?.mobile ||
                                                    selectedItem?.phone ||
                                                    "-"}
                                                </div>
                                              </div>
                                            </div>

                                            <div className="col-md-6 mb-3">
                                              <div className="p-3 border rounded-3 h-100">
                                                <small className="text-muted">
                                                  <i className="ri-bar-chart-2-line me-1"></i>
                                                  Status
                                                </small>
                                                <div className="mt-1">
                                                  <span
                                                    className={`badge px-3 py-2 ${
                                                      submissionData
                                                        ? "bg-success"
                                                        : "bg-danger"
                                                    }`}
                                                  >
                                                    {submissionData
                                                      ? "Submitted"
                                                      : "Pending"}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>

                                          {/* File */}
                                          {submissionData?.file && (
                                            <div className="mb-4">
                                              <h6 className="fw-bold mb-2">
                                                <i className="ri-attachment-2 me-1"></i>
                                                Submitted File
                                              </h6>
                                              <button
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={() => {
                                                  setFileUrl(
                                                    submissionData.file,
                                                  );
                                                  setShowImageModal(true);
                                                }}
                                              >
                                                <i className="ri-eye-line me-1"></i>
                                                View File
                                              </button>
                                            </div>
                                          )}

                                          {/* Answer */}
                                          <div className="mb-4">
                                            <h6 className="fw-bold mb-2">
                                              <i className="ri-file-text-line me-1"></i>
                                              Answer
                                            </h6>
                                            <div className="p-3 border rounded-3 bg-light">
                                              {submissionData?.answer ||
                                                "No Answer"}
                                            </div>
                                          </div>

                                          {/* Feedback */}
                                          <div>
                                            <h6 className="fw-bold mb-2">
                                              <i className="ri-chat-3-line me-1"></i>
                                              Feedback
                                            </h6>
                                            <div className="p-3 border rounded-3 bg-light">
                                              {submissionData?.feedback ||
                                                "No Feedback"}
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>

                                    <div className="modal-footer border-0 px-4 pb-4">
                                      <button
                                        className="btn text-white px-4"
                                        style={{
                                          background:
                                            "linear-gradient(135deg, #6a5af9, #8f6ed5)",
                                          borderRadius: "10px",
                                        }}
                                        onClick={() => {
                                          setShowDetailModal(false);
                                          setSubmissionData(null);
                                          setSelectedItem(null);
                                        }}
                                      >
                                        Close
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="modal-backdrop fade show"></div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
